import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, formatDate, API_URL } from "../lib/utils";
import { processedByText } from "../lib/fiscal";
import { EmailHistory } from "../components/common/EmailHistory";
import { CreditNoteDialog } from "../components/invoices/CreditNoteDialog";
import { toastApiError } from "../lib/apiErrors";
import { printInvoice, mergeLines } from "../lib/invoicePrint";
import { InvoiceCancelDialog } from "../components/invoices/InvoiceCancelDialog";
import { CancelCodesDialog } from "../components/invoices/CancelCodesDialog";
import { PaymentReferenceDialog } from "../components/invoices/PaymentReferenceDialog";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Eye,
  Receipt,
  CreditCard,
  Search,
  Filter,
  Calendar,
  Building,
  Undo2,
  Ban,
  KeyRound,
  Landmark,
  Mail,
} from "lucide-react";

const isCancelled = (inv) => inv.status === "A" || inv.status === "cancelled";

const InvoicesPage = () => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [createModal, setCreateModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [settings, setSettings] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_type: "FT",
    customer_name: "",
    customer_nif: "",
    customer_address: "",
    customer_phone: "",
    customer_email: "",
    payment_method: "NU",
    notes: "",
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [ncModal, setNcModal] = useState(false);
  const [ncInvoice, setNcInvoice] = useState(null);
  const [cancelInvoice, setCancelInvoice] = useState(null);
  const [codesModal, setCodesModal] = useState(false);
  const [refInvoice, setRefInvoice] = useState(null);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [invRes, ordRes, setRes] = await Promise.all([
        axios.get(`${API_URL}/invoices`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/orders?status=completed`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/settings`, { headers, withCredentials: true }),
      ]);
      setInvoices(invRes.data);
      setOrders(ordRes.data.filter(o => o.payment_status === "paid"));
      setSettings(setRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedOrder) {
      toast.error("Selecione um pedido");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/invoices`,
        {
          order_id: selectedOrder,
          ...invoiceForm,
        },
        { headers, withCredentials: true }
      );
      toast.success("Fatura criada com sucesso!");
      setCreateModal(false);
      setSelectedOrder("");
      setInvoiceForm({
        invoice_type: "FT",
        customer_name: "",
        customer_nif: "",
        customer_address: "",
        customer_phone: "",
        customer_email: "",
        payment_method: "NU",
        notes: "",
      });
      fetchData();
    } catch (error) {
      toastApiError(error, "Erro ao criar fatura");
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewModal(true);
  };

  const handlePrintInvoice = (invoice) => {
    printInvoice(invoice, settings || {});
  };

  const [emailing, setEmailing] = useState(null);
  const canEmail = (inv) => Boolean(inv.customer_email || inv.customer_ref_id || inv.credit_customer_id || (inv.customer_nif && inv.customer_nif !== "999999999"));
  const handleEmailInvoice = async (invoice) => {
    setEmailing(invoice.invoice_id);
    try {
      const r = await axios.post(`${API_URL}/invoices/${invoice.invoice_id}/email`, {}, { headers, withCredentials: true });
      toast.success(r.data.message || "Documento enviado");
      const patch = (inv) => (inv.invoice_id === invoice.invoice_id ? { ...inv, emails: [...(inv.emails || []), r.data.entry] } : inv);
      setInvoices((list) => list.map(patch));
      setSelectedInvoice((cur) => (cur && cur.invoice_id === invoice.invoice_id ? patch(cur) : cur));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar por email");
    } finally {
      setEmailing(null);
    }
  };

  const invoiceTypes = [
    { value: "FT", label: "Fatura (FT)", series: "FT" },
    { value: "FR", label: "Fatura-Recibo (FR)", series: "FR" },
    { value: "NC", label: "Nota de Crédito (NC)", series: "NC" },
  ];

  const filteredInvoices = invoices.filter((inv) => {
    const matchesType = filter === "all" || inv.invoice_type === filter;
    const matchesSearch =
      inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.customer_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.customer_nif || "").includes(searchQuery);
    return matchesType && matchesSearch;
  });

  const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("invoices")}</h1>
          <p className="text-muted-foreground">Gestão de documentos fiscais</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => setCodesModal(true)} className="gap-2" data-testid="cancel-codes-btn">
              <KeyRound className="w-4 h-4" />
              Códigos de Anulação
            </Button>
          )}
          <Button onClick={() => setCreateModal(true)} className="gap-2" data-testid="create-invoice-btn">
            <FileText className="w-4 h-4" />
            Nova Fatura
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{invoices.length}</p>
              <p className="text-sm text-muted-foreground">Total Documentos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Receipt className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{invoices.filter((i) => i.invoice_type === "FT").length}</p>
              <p className="text-sm text-muted-foreground">Faturas (FT)</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/10">
              <CreditCard className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{invoices.filter((i) => i.invoice_type === "FR").length}</p>
              <p className="text-sm text-muted-foreground">Faturas-Recibo (FR)</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/10">
              <Building className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold font-mono">{formatCurrency(totalInvoiced)}</p>
              <p className="text-sm text-muted-foreground">Total Faturado</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por nº, cliente ou NIF..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {invoiceTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-medium">Nº Documento</th>
                  <th className="text-left p-4 font-medium">Tipo</th>
                  <th className="text-left p-4 font-medium">Cliente</th>
                  <th className="text-left p-4 font-medium">NIF</th>
                  <th className="text-left p-4 font-medium">Data</th>
                  <th className="text-right p-4 font-medium">Total</th>
                  <th className="text-right p-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.invoice_id} className={`border-b hover:bg-muted/30 ${isCancelled(invoice) ? "opacity-60" : ""}`} data-testid={`invoice-row-${invoice.invoice_id}`}>
                    <td className="p-4 font-mono font-medium">
                      <span className={isCancelled(invoice) ? "line-through" : ""}>{invoice.invoice_number}</span>
                      {isCancelled(invoice) && <Badge className="ml-2 bg-red-600 text-white hover:bg-red-600" data-testid={`invoice-cancelled-badge-${invoice.invoice_id}`}>ANULADA</Badge>}
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {invoiceTypes.find((t) => t.value === invoice.invoice_type)?.series || invoice.invoice_type}
                      </Badge>
                    </td>
                    <td className="p-4">{invoice.customer_name || "Consumidor Final"}</td>
                    <td className="p-4 font-mono text-muted-foreground">{invoice.customer_nif || "-"}</td>
                    <td className="p-4 text-muted-foreground">{formatDate(invoice.created_at)}</td>
                    <td className="p-4 text-right font-mono font-medium">{formatCurrency(invoice.total)}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" title="Ver fatura" onClick={() => handleViewInvoice(invoice)} data-testid={`invoice-view-${invoice.invoice_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Imprimir / PDF" onClick={() => handlePrintInvoice(invoice)} data-testid={`invoice-print-${invoice.invoice_id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="relative" title={(invoice.emails || []).length ? `Enviada ${invoice.emails.length}× · último para ${invoice.emails[invoice.emails.length - 1].to}` : invoice.customer_email ? `Enviar por email (${invoice.customer_email})` : canEmail(invoice) ? "Enviar por email ao cliente" : "Consumidor final — sem cliente/email associado"} disabled={emailing === invoice.invoice_id || !canEmail(invoice)} onClick={() => handleEmailInvoice(invoice)} data-testid={`invoice-email-${invoice.invoice_id}`}>
                          <Mail className={`w-4 h-4 ${emailing === invoice.invoice_id ? "animate-pulse" : canEmail(invoice) ? "text-sky-600" : "text-muted-foreground/40"}`} />
                          {(invoice.emails || []).length > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-sky-600 text-white text-[9px] font-bold leading-[14px] text-center" data-testid={`invoice-email-count-${invoice.invoice_id}`}>{invoice.emails.length}</span>}
                        </Button>
                        {(invoice.is_credit || invoice.notes === "Venda a crédito") && !isCancelled(invoice) && !invoice.paid_via_reference && invoice.payment_status !== "paid" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Referência de pagamento (AppyPay)"
                            onClick={() => setRefInvoice(invoice)}
                            data-testid={`payment-reference-btn-${invoice.invoice_id}`}
                          >
                            <Landmark className="w-4 h-4 text-emerald-600" />
                          </Button>
                        )}
                        {invoice.paid_via_reference && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 self-center" data-testid={`paid-by-reference-${invoice.invoice_id}`}>Paga · Ref.</Badge>}
                        {invoice.invoice_type !== "NC" && !invoice.fully_credited && !isCancelled(invoice) && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Devolução / Nota de Crédito"
                              onClick={() => { setNcInvoice(invoice); setNcModal(true); }}
                              data-testid={`credit-note-btn-${invoice.invoice_id}`}
                            >
                              <Undo2 className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Anular fatura (requer código de anulação)"
                              onClick={() => setCancelInvoice(invoice)}
                              data-testid={`cancel-invoice-btn-${invoice.invoice_id}`}
                            >
                              <Ban className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma fatura encontrada</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-lg" data-testid="create-invoice-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Nova Fatura</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Pedido</Label>
              <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar pedido pago..." />
                </SelectTrigger>
                <SelectContent>
                  {orders.map((order) => (
                    <SelectItem key={order.order_id} value={order.order_id}>
                      #{order.order_number} - {formatCurrency(order.total)} - {formatDate(order.created_at)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Documento</Label>
              <p className="text-sm text-muted-foreground rounded-md border p-2" data-testid="invoice-type-auto-hint">
                Determinado pelo sistema: <b>FR — Factura-Recibo</b> se o pedido já está pago, <b>FT — Factura</b> se fica a crédito. Notas de crédito emitem-se a partir da factura original.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Nome do Cliente</Label>
              <Input
                value={invoiceForm.customer_name}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_name: e.target.value })}
                placeholder="Consumidor Final"
              />
            </div>

            <div className="space-y-2">
              <Label>NIF</Label>
              <Input
                value={invoiceForm.customer_nif}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_nif: e.target.value })}
                placeholder="123456789"
              />
            </div>

            <div className="space-y-2">
              <Label>Morada</Label>
              <Input
                value={invoiceForm.customer_address}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_address: e.target.value })}
                placeholder="Rua..., Lisboa"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateModal(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleCreateInvoice} data-testid="confirm-create-invoice-btn">
              Criar Fatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={viewModal} onOpenChange={setViewModal}>
        <DialogContent className="max-w-2xl" data-testid="view-invoice-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6">
              {isCancelled(selectedInvoice) && (
                <div className="rounded-lg border-2 border-red-500/40 bg-red-500/5 p-4 text-sm space-y-1" data-testid="invoice-cancellation-block">
                  <p className="font-bold text-red-600 flex items-center gap-2"><Ban className="w-4 h-4" /> DOCUMENTO ANULADO</p>
                  <p><span className="text-muted-foreground">Motivo:</span> {selectedInvoice.cancel_reason || selectedInvoice.cancellation?.reason || "—"}</p>
                  <p><span className="text-muted-foreground">Anulada por:</span> {selectedInvoice.cancellation?.cancelled_by_name || selectedInvoice.cancelled_by_name || "—"} · {formatDate(selectedInvoice.cancelled_at)}</p>
                  {selectedInvoice.cancellation?.code_generated_by_name && (
                    <p><span className="text-muted-foreground">Código autorizado por:</span> {selectedInvoice.cancellation.code_generated_by_name}{selectedInvoice.cancellation.code_source === "platform" ? " (Backoffice LUMORA)" : " (administrador)"} · {formatDate(selectedInvoice.cancellation.code_generated_at)}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Cliente</h4>
                  <p className="font-medium">{selectedInvoice.customer_name || "Consumidor Final"}</p>
                  {selectedInvoice.customer_nif && <p className="text-sm">NIF: {selectedInvoice.customer_nif}</p>}
                  {selectedInvoice.customer_address && <p className="text-sm text-muted-foreground">{selectedInvoice.customer_address}</p>}
                </div>
                <div className="text-right">
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Documento</h4>
                  <Badge>{invoiceTypes.find((t) => t.value === selectedInvoice.invoice_type)?.label}</Badge>
                  <p className="text-sm mt-1">{formatDate(selectedInvoice.created_at)}</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left p-3 text-sm">Produto</th>
                      <th className="text-center p-3 text-sm">Qtd</th>
                      <th className="text-right p-3 text-sm">Preço</th>
                      <th className="text-center p-3 text-sm">IVA</th>
                      <th className="text-right p-3 text-sm">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergeLines(selectedInvoice.items).map((item, index) => (
                      <tr key={index} className="border-t" data-testid={`invoice-item-${index}`}>
                        <td className="p-3">{item.product_name}{item.discount_amount > 0 && <div className="text-xs text-red-600">Desconto{item.promotion_name ? ` (${item.promotion_name})` : ""}: −{formatCurrency(item.discount_amount)}</div>}</td>
                        <td className="p-3 text-center">{item.quantity} <span className="text-xs text-muted-foreground">{item.unit || "un."}</span></td>
                        <td className="p-3 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                        <td className="p-3 text-center text-xs text-muted-foreground">{item.tax_rate != null ? `${item.tax_rate}%` : "—"}</td>
                        <td className="p-3 text-right font-mono">{formatCurrency(item.line_total ?? item.quantity * item.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IVA</span>
                    <span className="font-mono">{formatCurrency(selectedInvoice.tax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span className="font-mono text-primary">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                  {selectedInvoice.amount_received ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Valor Recebido</span>
                        <span className="font-mono">{formatCurrency(selectedInvoice.amount_received)}</span>
                      </div>
                      {selectedInvoice.change_given > 0 && (
                        <div className="flex justify-between text-sm font-semibold text-emerald-600">
                          <span>Troco</span>
                          <span className="font-mono">{formatCurrency(selectedInvoice.change_given)}</span>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </div>

              {selectedInvoice.qr_code_data && (
                <div className="flex items-center gap-4 p-4 rounded-lg border bg-muted/30" data-testid="invoice-qr-block">
                  <img src={selectedInvoice.qr_code_data} alt="QR Code" className="w-24 h-24" />
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="text-sm font-semibold text-foreground">Verificação do Documento</p>
                    <p>Leia o código QR para validar este documento.</p>
                    <p className="font-mono break-all" data-testid="invoice-processed-text">{processedByText(selectedInvoice)}</p>
                    {selectedInvoice.training && <p className="font-bold text-amber-600">DOCUMENTO DE FORMAÇÃO — SEM VALOR FISCAL</p>}
                  </div>
                </div>
              )}
              <EmailHistory emails={selectedInvoice.emails} testId="invoice-email-history" />
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setViewModal(false)} data-testid="invoice-detail-close-btn">
              Fechar
            </Button>
            <Button variant="outline" onClick={() => handleEmailInvoice(selectedInvoice)} disabled={!selectedInvoice || emailing === selectedInvoice?.invoice_id || !canEmail(selectedInvoice || {})} className="gap-2" data-testid="invoice-detail-email-btn">
              <Mail className="w-4 h-4" />
              {emailing === selectedInvoice?.invoice_id ? "A enviar…" : "Enviar por email"}
            </Button>
            <Button onClick={() => handlePrintInvoice(selectedInvoice)} className="gap-2" data-testid="invoice-detail-print-btn">
              <Download className="w-4 h-4" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Note Modal */}
      <InvoiceCancelDialog invoice={cancelInvoice} open={!!cancelInvoice} onClose={() => setCancelInvoice(null)} headers={headers} onDone={fetchData} />
      <CancelCodesDialog open={codesModal} onClose={() => setCodesModal(false)} headers={headers} />
      <PaymentReferenceDialog invoice={refInvoice} open={!!refInvoice} onClose={() => setRefInvoice(null)} headers={headers} onPaid={fetchData} />
      <CreditNoteDialog invoice={ncModal ? ncInvoice : null} open={ncModal} onClose={() => setNcModal(false)} headers={headers} onDone={fetchData} />
    </div>
  );
};

export default InvoicesPage;
